import type { FormatConfig } from '../models/chart-config.model';
import {
	buildComparisonStatusHtml,
	buildPreviousWithPercentHtml,
	COMPARISON_NEAR_ZERO_THRESHOLD,
	formatComparisonPercentInParens,
	resolveComparisonDisplayState,
	resolveComparisonPolarityColor,
} from './comparison-display-format.util';
import { formatValue, type FormatValueContext } from './table-grid-column.util';

export const TABLE_COMPARISON_NEAR_ZERO_THRESHOLD =
	COMPARISON_NEAR_ZERO_THRESHOLD;

export interface TableStackedComparisonCellParams {
	current: number | null;
	previous: number | null;
	format?: FormatConfig | null;
	formatCtx?: FormatValueContext;
	inverseMetric?: boolean;
}

/** Formats a metric value for table cells (matches grid column `format`). */
export function formatTableMetricDisplayValue(
	value: number,
	format?: FormatConfig | null,
	ctx?: FormatValueContext
): string {
	return formatValue(value, format, ctx);
}

/**
 * Lower-is-better metrics (CPA, CPC, bounce rate, …) until schema exposes polarity.
 * Matches on field name / column id segment (case-insensitive).
 */
export function resolveInverseMetricForField(
	fieldName?: string | null
): boolean {
	const raw = fieldName?.trim();
	if (!raw) {
		return false;
	}
	const normalized = raw.toLowerCase();
	const segment = normalized.split('.').pop() ?? normalized;
	const inversePatterns = [
		/^cpa$/,
		/^cpc$/,
		/^cpm$/,
		/^cost$/,
		/costper/,
		/cost_per/,
		/bouncerate/,
		/bounce_rate/,
		/cost$/,
		/\.cpa$/,
		/\.cpc$/,
		/\.cpm$/,
	];
	return inversePatterns.some(
		pattern => pattern.test(normalized) || pattern.test(segment)
	);
}

export function computeComparisonPercentChange(
	current: number,
	previous: number
): number | null {
	return resolveComparisonDisplayState(current, previous).percentChange;
}

/** Sort key for percent change when comparison is stacked (nulls last). */
export function tableComparisonSortValue(
	current: number | null,
	previous: number | null
): number | null {
	return resolveComparisonDisplayState(current, previous).percentChange;
}

/** Change label: +/- encodes direction, colour encodes impact (polarity-aware). */
export function buildComparisonChangeBadgeHtml(
	percentChange: number,
	inverseMetric?: boolean
): string {
	const color = resolveComparisonPolarityColor(percentChange, inverseMetric);
	const pctText = formatComparisonPercentInParens(percentChange, 1);
	return `<span style="font-size:var(--font-size-xs);color:${color};white-space:nowrap;">${pctText}</span>`;
}

/**
 * Stacked comparison cell: primary value plus New / No data / previous+percent.
 * Keeps column count stable — `__prev` data is read from the row, not shown as a column.
 */
export function buildTableStackedComparisonCellHtml(
	params: TableStackedComparisonCellParams
): string {
	const { current, previous, format, formatCtx, inverseMetric } = params;
	const state = resolveComparisonDisplayState(current, previous);

	const primaryBlock = `<span style="font-weight:var(--font-weight-semibold);color:var(--text-primary-color);">${formatTableMetricDisplayValue(state.displayCurrent, format, formatCtx)}</span>`;

	let comparisonLabel = '';
	if (state.kind === 'new') {
		comparisonLabel = buildComparisonStatusHtml('new');
	} else if (state.kind === 'no-data') {
		comparisonLabel = buildComparisonStatusHtml('no-data');
	} else {
		comparisonLabel = buildPreviousWithPercentHtml({
			previousFormatted: formatTableMetricDisplayValue(
				state.displayPrevious,
				format,
				formatCtx
			),
			percentChange: state.percentChange ?? 0,
			decimals: 1,
			inverseMetric,
		});
	}

	const comparisonRow = `<div style="display:flex;align-items:center;justify-content:flex-end;width:100%;">${comparisonLabel}</div>`;

	return `<div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;width:100%;">${primaryBlock}${comparisonRow}</div>`;
}
